import React, { useCallback, useRef, useState } from 'react'
import Header from './components/Layout/Header.jsx'
import FluxMap from './components/Map/FluxMap.jsx'
import AssessControls from './components/Controls/AssessControls.jsx'
import ScanControls from './components/Controls/ScanControls.jsx'
import AssessmentPanel from './components/Panel/AssessmentPanel.jsx'
import ScanResultsPanel from './components/Panel/ScanResultsPanel.jsx'
import ComparePanel from './components/Panel/ComparePanel.jsx'
import { useAssessment } from './hooks/useAssessment.js'
import AboutPanel from './components/Panel/AboutPanel.jsx'

export default function App() {
  const [activeTab,  setActiveTab]  = useState('assess')
  const [lastClick,  setLastClick]  = useState(null)   // {lat, lng}
  const [country,    setCountry]    = useState('indonesia')
  const [technology, setTechnology] = useState('solar_pv')
  const [capacityMw, setCapacityMw] = useState(10)
  const mapRef = useRef(null)

  const {
    assessment, assessLoading, assessError,
    scanResult,  scanLoading,  scanError,
    compareResult, compareLoading, compareError,
    compareQueue,
    runAssessment, runAreaScan, runComparison,
    addToCompareQueue, removeFromCompareQueue, clearCompareQueue,
  } = useAssessment()

  // Track which assessment has a visible score badge
  const [assessmentMarker, setAssessmentMarker] = useState(null)

  // Handle map clicks
  const handleMapClick = useCallback((lngLat) => {
    setLastClick(lngLat)

    if (activeTab === 'compare') {
      const label = `Site ${compareQueue.length + 1}`
      addToCompareQueue({ lat: lngLat.lat, lon: lngLat.lng, label })
    }
  }, [activeTab, compareQueue.length, addToCompareQueue])

  // Run assessment from controls
  const handleAssess = useCallback(async (params) => {
    const result = await runAssessment({
      latitude:    params.lat,
      longitude:   params.lon,
      technology:  params.technology,
      capacity_mw: params.capacity_mw,
      country:     params.country,
    })
    if (result) {
      setAssessmentMarker({
        lat:   params.lat,
        lon:   params.lon,
        score: result.scores?.overall_score ?? 0,
      })
    }
  }, [runAssessment])

  // Click on scan point → run full assessment
  const handleScanPointClick = useCallback(({ lat, lng }) => {
    setActiveTab('assess')
    setLastClick({ lat, lng })
    runAssessment({
      latitude: lat, longitude: lng,
      technology, capacity_mw: capacityMw, country,
    })
  }, [runAssessment, technology, capacityMw, country])

  // Fly map to a location
  const handleFlyTo = useCallback((center) => {
    mapRef.current?._flyTo?.(center)
  }, [])

  // Run comparison
  const handleRunCompare = useCallback(() => {
    runComparison({
      sites: compareQueue.map(s => ({
        latitude: s.lat, longitude: s.lon, label: s.label,
      })),
      technology,
      capacity_mw: capacityMw,
      country,
    })
  }, [runComparison, compareQueue, technology, capacityMw, country])

  return (
    <div className="app">
      {/* Header */}
      <Header activeTab={activeTab} onTabChange={setActiveTab} />

      {/* Sidebar */}
      <aside className="sidebar app__sidebar">
        {activeTab === 'assess' && (
          <>
            <AssessControls
              lastClick={lastClick}
              loading={assessLoading}
              onAssess={handleAssess}
            />
            <AssessmentPanel
              assessment={assessment}
              loading={assessLoading}
              error={assessError}
            />
          </>
        )}

        {activeTab === 'scan' && (
          <>
            <ScanControls
              loading={scanLoading}
              onScan={runAreaScan}
              onFlyTo={handleFlyTo}
            />
            <ScanResultsPanel
              scanResult={scanResult}
              scanLoading={scanLoading}
              scanError={scanError}
            />
          </>
        )}

        {activeTab === 'compare' && (
          <ComparePanel
            queue={compareQueue}
            onRemove={removeFromCompareQueue}
            onClear={clearCompareQueue}
            onRunCompare={handleRunCompare}
            compareResult={compareResult}
            compareLoading={compareLoading}
            compareError={compareError}
            technology={technology}
            country={country}
            capacityMw={capacityMw}
          />
        )}

        {activeTab === 'about' && <AboutPanel onFlyTo={handleFlyTo} />}
      </aside>

      {/* Map */}
      <div ref={mapRef} style={{ position: 'relative' }} className="app__map">
        <FluxMap
          activeTab={activeTab}
          onMapClick={handleMapClick}
          markerPos={lastClick}
          assessmentMarker={assessmentMarker}
          scanResult={activeTab === 'scan' ? scanResult : null}
          compareQueue={activeTab === 'compare' ? compareQueue : []}
          onScanPointClick={handleScanPointClick}
        />
      </div>
    </div>
  )
}
